// AIDEV-NOTE: Pure, deterministic layout for the DPMP® Parts Map (Michelle Glass's format —
// docs/plans/2026-09-01-feat-dpmp-parts-map-plan.md). Not a graph layout: parts sit in
// concentric zones by classification (exiles centre, managers middle band, firefighters outer
// band, guides / unattached burdens on the border; Self is the white space) and relations are
// per-part SPOKES pointing toward the related part, so nothing crosses by construction.
// Same input → same output: nodes are ordered by name within a zone, no RNG, and the
// collision-relaxation pass runs a fixed number of iterations in a fixed order.
//
// AIDEV-NOTE: The layout is computed in PIXEL space (Adam, Sep 2026): the rings scale with
// the rendered size, but avatars, labels and padding are fixed pixel sizes (capped), so a
// larger canvas becomes space BETWEEN parts instead of bigger parts. `size` defaults to 1000
// for tests and server renders; the component measures its container and re-lays out.

export type DpmpMapZone =
    | "exile"
    | "manager"
    | "firefighter"
    | "protector-boundary"
    | "unknown-boundary"
    | "guide"
    | "unattached-burden";

export interface DpmpMapNodeInput {
    id: string;
    name: string;
    type: string | null | undefined;
    imageUrl?: string | null;
}

export interface DpmpMapEdgeInput {
    sourcePartId: string;
    targetPartId: string;
    type: string;
}

export interface DpmpMapSpoke {
    targetId: string;
    /** Text set along the spoke, e.g. "Critic (P)". Ellipsised at the END. */
    label: string;
    mark: "" | "P" | "A";
    type: string;
    /**
     * True when THIS node is the edge's sourcePartId.
     *
     * AIDEV-NOTE: Every edge emits a spoke on both ends (see the addSpoke pair below), so
     * a renderer drawing an arrow from "the node it found the spoke on" points a
     * `protects` from the exile back at its protector half the time. The direction has to
     * come from the edge, not from traversal order.
     */
    fromSource: boolean;
    /** Radians, screen coordinates (y down). */
    angle: number;
    /** Length beyond the node edge, px — wide enough to back the label. */
    length: number;
}

export interface DpmpMapNode {
    id: string;
    name: string;
    imageUrl: string | null;
    zone: DpmpMapZone;
    x: number;
    y: number;
    radius: number;
    /** Sits ON a ring line — classification unknown or dual-role. */
    onLine: boolean;
    /** Drawn as a cloud on the border (guides, unattached burdens). */
    cloud: boolean;
    dashed: boolean;
    spokes: DpmpMapSpoke[];
}

export interface DpmpMapLayout {
    /** Rendered size in px (square). */
    size: number;
    center: number;
    rings: { exile: number; manager: number; outer: number; border: number };
    nodeRadius: number;
    /** Spoke label size (px), smaller on dense maps so spokes stay near the node. */
    spokeFontSize: number;
    /** Name label size (px), smaller on dense maps. */
    nameFontSize: number;
    /** Name labels are truncated to this many characters. */
    nameMaxChars: number;
    nodes: DpmpMapNode[];
    zoneCounts: Record<DpmpMapZone, number>;
    /** How many name pills overlap another pill — drives the avatar-size retry. */
    labelCollisions: number;
}

export interface DpmpMapLayoutOptions {
    /** Rendered size in px. Defaults to 1000. */
    size?: number;
    /** Avatars never grow past this, however much room there is. Defaults to 30. */
    maxNodeRadius?: number;
    /**
     * Cap on characters per name-pill line.
     * AIDEV-NOTE: Pill WIDTH is what collides, and it is driven by characters, not avatar
     * size — shrinking avatars on a small canvas without shrinking the labels just leaves
     * the same wide pills with more room to miss each other. Scoped maps draw real
     * connector lines, so a name only has to identify a part the reader can also click.
     */
    nameMaxChars?: number;
    /** Which surface this map is for — selects the tuning preset. Defaults to "system". */
    context?: DpmpMapContext;
}

/**
 * Per-context layout tuning.
 *
 * AIDEV-NOTE: These knobs are NOT universal. They were tuned on scoped maps (a session's
 * handful of parts, or one part's neighbourhood) and quietly wrecked the whole-system map,
 * which has to hold 50+ parts: a ring overflowing the canvas is a reasonable trade when
 * three firefighters need corner room, and a disaster when twenty-five managers do.
 *
 * Keep every context-sensitive number HERE rather than as a module constant, so a change
 * for one surface cannot silently reshape the others. Add a field rather than reaching for
 * a literal inside the solver.
 */
export type DpmpMapContext = "system" | "session" | "part";

export interface DpmpMapTuning {
    /** Manager ring as a multiple of the inscribed circle. >1 clips at the four sides. */
    managerRingOverflow: number;
    /** Clearance between the outermost avatar and the page edge, as a fraction of size. */
    edgeMarginFraction: number;
    /** Breathing room above bare geometric fit for the exile disc. */
    exileComfort: number;
    /** Ceiling on the exile disc as a share of the manager ring. */
    exileMaxShare: number;
    /** Exile ring floor as a multiple of one avatar radius. */
    minExileAvatarMultiple: number;
    /** Exile ring floor as a fraction of the canvas. */
    minExileFraction: number;
    /** Guides, burdens and firefighters go to the corners instead of concentric lanes. */
    cornerOverflowZones: boolean;
    /** Reserve the upper-left arc so no part sits under the EXILES ring label. */
    reserveRingLabelArc: boolean;
    /**
     * Radius of the guides / unattached-burden lane, as a fraction of size.
     * AIDEV-NOTE: Must leave room for a name pill inside the page, or the clamp drags
     * these clouds back INSIDE the manager ring they are supposed to sit beyond.
     */
    borderFraction: number;
    /**
     * Name-pill character cap by part DENSITY — parts normalised to a 1000px canvas
     * (`count · (1000/size)²`), first match wins.
     * AIDEV-NOTE: Pill WIDTH is the collision driver, and width is characters — not avatar
     * size. The density retry only ever stepped the avatar tier down, so a crowded map
     * bottomed out at the smallest avatar while the pills stayed exactly as wide and kept
     * colliding. Shortening the name is the lever that actually works.
     */
    nameCharSteps: Array<{ upTo: number; chars: number }>;
    /**
     * Start with names hidden once this share of pills still collide (null = never).
     * AIDEV-NOTE: Past a certain density no amount of shortening or nudging makes 55 name
     * pills readable at once — the honest move is to lead with the shape and let the
     * reader turn names on. The map already does exactly this for relations, and says so
     * in the legend.
     */
    autoHideNamesCollisionRatio: number | null;
}

export const DPMP_MAP_TUNING: Record<DpmpMapContext, DpmpMapTuning> = {
    // The whole catalog. Everything has to FIT: the ring stays inside the canvas, the
    // exile disc is capped hard, and overflow zones keep their concentric lanes because at
    // this density the corners cannot absorb them.
    system: {
        // AIDEV-NOTE: >1 on purpose. Adam's standing instruction is (A) the exile disc gets
        // exactly enough room for its own count and NOT ONE PIXEL MORE, then (B) the manager
        // ring is as large as it can be while still leaving the four corners for firefighters
        // and border clouds. A ring at 0.66 of the inscribed circle — which is what this was —
        // leaves a third of the canvas empty on every side and squeezes 25 managers into a
        // thin annulus. The ring is meant to clip at the four SIDES; that is the space the
        // square cannot otherwise use.
        managerRingOverflow: 1.06,
        edgeMarginFraction: 0.055,
        exileComfort: 1.15,
        exileMaxShare: 0.5,
        minExileAvatarMultiple: 2,
        minExileFraction: 0.075,
        cornerOverflowZones: true,
        reserveRingLabelArc: true,
        borderFraction: 0.44,
        nameCharSteps: [
            { upTo: 20, chars: 18 },
            { upTo: 40, chars: 16 },
            { upTo: 75, chars: 10 },
            { upTo: Number.POSITIVE_INFINITY, chars: 8 },
        ],
        autoHideNamesCollisionRatio: 0.18,
    },
    // One session: a handful of parts, so spend the corners and let the ring clip.
    session: {
        managerRingOverflow: 1.18,
        edgeMarginFraction: 0.055,
        exileComfort: 1.7,
        exileMaxShare: 0.55,
        minExileAvatarMultiple: 3,
        minExileFraction: 0.075,
        cornerOverflowZones: true,
        reserveRingLabelArc: true,
        borderFraction: 0.455,
        nameCharSteps: [
            { upTo: 10, chars: 18 },
            { upTo: 20, chars: 14 },
            { upTo: Number.POSITIVE_INFINITY, chars: 11 },
        ],
        // Safety valve only — scoped maps are small canvases, and past a point the pills
        // cannot be made to fit however short the names get.
        autoHideNamesCollisionRatio: 0.2,
    },
    // One part plus its direct relations — the sparsest map of the three.
    part: {
        managerRingOverflow: 1.18,
        edgeMarginFraction: 0.055,
        exileComfort: 1.7,
        exileMaxShare: 0.55,
        minExileAvatarMultiple: 3,
        minExileFraction: 0.075,
        cornerOverflowZones: true,
        reserveRingLabelArc: true,
        borderFraction: 0.455,
        nameCharSteps: [
            { upTo: 8, chars: 16 },
            { upTo: Number.POSITIVE_INFINITY, chars: 12 },
        ],
        autoHideNamesCollisionRatio: 0.2,
    },
};

export const DPMP_MAP_DEFAULT_SIZE = 1000;

// AIDEV-NOTE: Michelle's paper map has TWO drawn rings, not three: exiles innermost,
// managers in the middle ring, and firefighters loose in the space outside the manager
// ring with no container at all ("firefighters don't like to be contained"). Guides and
// unattached burdens sit further out still, near the paper's edge. `outer` is therefore
// the outer BOUND of the firefighter area — it is never stroked, it only bounds placement
// and carries the zone label's invisible text path.
/**
 * The zone label ("EXILES") is set along the ring at 12% of its path, which starts at the
 * left and sweeps clockwise — i.e. the upper-left arc. Parts are kept out of that window
 * so nothing is ever laid over the label.
 *
 * AIDEV-NOTE: Keep in lockstep with the `startOffset` on the ring <textPath> in
 * dpmp-parts-map.tsx. Screen coords are y-down, so θ=π is the left of the circle and
 * increasing θ runs clockwise on screen.
 */
const RING_LABEL_ANGLE = Math.PI + 0.12 * 2 * Math.PI;
/**
 * Ceiling on the reserved window, for rings too small to hold the word any other way.
 *
 * AIDEV-NOTE: This used to be THE reservation — a flat 1.15 rad (66°) blanked on every
 * sub-ring of every band, which is what carved the empty pizza slice out of the upper-left
 * quadrant and crowded every part into the remaining three. Two things were wrong: the
 * label is a fixed-width WORD, so the angle it subtends shrinks as the ring grows (1.15 rad
 * at r=590 reserves 680px of arc for a 90px word), and it rides ONE circle — the band's
 * outer boundary — so sub-rings deeper inside the band were never under it. Both are now
 * measured: `ringLabelArcAt` for the width, `labelledSubRing` for the depth.
 */
const RING_LABEL_MAX_ARC = 1.15;
/** Longest ring label; every band reserves the same window so the wedge stays tidy. */
const RING_LABEL_TEXT = "FIREFIGHTERS";
const RING_LABEL_FONT_SIZE = 10.5;
const RING_LABEL_LETTER_SPACING = 1.5;
/** Arc length the widest ring label needs, plus breathing room at both ends. */
function ringLabelArcLength(): number {
    return (
        estimateTextWidth(RING_LABEL_TEXT, RING_LABEL_FONT_SIZE) +
        RING_LABEL_TEXT.length * RING_LABEL_LETTER_SPACING +
        24
    );
}

/** Angular window the ring label occupies at `radius` — narrower the further out it sits. */
function ringLabelArcAt(radius: number): number {
    return Math.min(
        RING_LABEL_MAX_ARC,
        ringLabelArcLength() / Math.max(radius, 1),
    );
}

/**
 * Is a sub-ring at `radius` close enough to the labelled circle at `labelRadius` to run
 * under the text? Only that sub-ring reserves the window; the rest of the band fills up.
 */
function underRingLabel(
    radius: number,
    labelRadius: number,
    nodeRadius: number,
): boolean {
    return Math.abs(radius - labelRadius) < nodeRadius + RING_LABEL_FONT_SIZE;
}
/**
 * The "Self — around all parts" caption, as a keep-out BOX rather than a full-width strip.
 *
 * AIDEV-NOTE: It is set at the bottom-LEFT (x=14, y=size-14 in the renderer — keep in
 * lockstep). Reserving the whole bottom edge for it cost the map a 46px band across its
 * widest span and made the post-relax clamp drag the entire bottom arc upward into itself,
 * which is what squashed the managers into each other. Only nodes actually over the words
 * need to move.
 */
const CAPTION_FONT_SIZE = 10.5;
const CAPTION_LETTER_SPACING = 1.5;
const CAPTION_TEXT = "SELF — AROUND ALL PARTS";
const CAPTION_GAP = 8;

function captionBox(size: number): Box {
    const width =
        estimateTextWidth(CAPTION_TEXT, CAPTION_FONT_SIZE) +
        CAPTION_TEXT.length * CAPTION_LETTER_SPACING;
    return {
        x0: 0,
        y0: size - 14 - CAPTION_FONT_SIZE - CAPTION_GAP,
        x1: 14 + width + CAPTION_GAP,
        y1: size,
    };
}
/** The four diagonals, where the square leaves room outside the inscribed circle. */
// AIDEV-NOTE: Ordered so the BOTTOM-LEFT corner is filled last — that is where the
// "Self — around all parts" caption sits, and a cornered part landing there
// collides with it. Screen coords are y-down: 7π/4 is top-right, 3π/4 bottom-left.
const CORNER_ANGLES = [
    (7 * Math.PI) / 4,
    (5 * Math.PI) / 4,
    Math.PI / 4,
    (3 * Math.PI) / 4,
];

/**
 * Furthest a node may sit along a diagonal and still clear the square's edge. The corners
 * reach size/2·√2 from the centre, so this is meaningfully further out than the inscribed
 * manager ring — which is the whole point: it is space the circle cannot use.
 */
function cornerLimit(
    size: number,
    nodeRadius: number,
    edgeMarginFraction: number,
): number {
    const half = size / 2 - size * edgeMarginFraction - nodeRadius;
    return half / Math.cos(Math.PI / 4);
}

const SPOKE_MIN_LENGTH = 30;
const SPOKE_MAX_LENGTH = 110;
const SPOKE_MIN_SEPARATION = 0.34;

/** Clear space (px) between avatar edges — room for the name label. */
const NODE_PADDING = 14;
const RELAX_ITERATIONS = 240;
const RADIUS_TIERS = [30, 26, 22, 18, 16, 14];

export const DPMP_ZONE_LABELS: Record<DpmpMapZone, string> = {
    exile: "Exile",
    manager: "Manager",
    firefighter: "Firefighter",
    "protector-boundary": "Protector — manager or firefighter not yet clear",
    "unknown-boundary": "Not yet classified",
    guide: "Guide",
    "unattached-burden": "Unattached burden",
};

/** Stored `part.type` string → map zone. Case-insensitive; unknown values land on a line. */
export function resolveDpmpMapZone(
    type: string | null | undefined,
): DpmpMapZone {
    const t = (type ?? "").trim().toLowerCase();
    if (t === "exile") return "exile";
    // AIDEV-NOTE: Self-like parts have no zone in Michelle's legend (Self is the white
    // space); IFS treats them as managers, so they share the middle band. Open question.
    if (t === "protector (manager)" || t === "protector (self-like)") {
        return "manager";
    }
    if (t === "protector (firefighter)") return "firefighter";
    if (t === "protector") return "protector-boundary";
    if (t === "guide") return "guide";
    if (t.startsWith("unattached burden")) return "unattached-burden";
    return "unknown-boundary";
}

const EMPTY_COUNTS: Record<DpmpMapZone, number> = {
    exile: 0,
    manager: 0,
    firefighter: 0,
    "protector-boundary": 0,
    "unknown-boundary": 0,
    guide: 0,
    "unattached-burden": 0,
};

/** Zone counts alone — for the key, which doesn't need a full layout. */
export function computeDpmpZoneCounts(
    nodes: Pick<DpmpMapNodeInput, "type">[],
): Record<DpmpMapZone, number> {
    const counts = { ...EMPTY_COUNTS };
    for (const node of nodes) counts[resolveDpmpMapZone(node.type)] += 1;
    return counts;
}

// AIDEV-NOTE: Dense maps need smaller, shorter labels or spokes run many node-widths long
// and cross their neighbours. Everything keys off the node radius the layout picked.
function spokeFontSizeFor(nodeRadius: number): number {
    return nodeRadius <= 18 ? 10 : 11;
}

function spokeLabelMaxCharsFor(nodeRadius: number): number {
    return nodeRadius <= 18 ? 16 : 22;
}

function nameFontSizeFor(nodeRadius: number): number {
    return nodeRadius <= 18 ? 10 : 11.5;
}

function nameMaxCharsFor(nodeRadius: number): number {
    return nodeRadius <= 18 ? 14 : 18;
}

// AIDEV-NOTE: Spacing is driven by the NAME PILL, not the avatar. Pills are roughly four
// times an avatar's width, so sizing zones from the avatar (as this did) produced rings
// that were geometrically "big enough" and visibly overlapping — shrinking avatars on a
// crowded map then bought almost nothing, because the colliding element never changed size.
function nodeSpacingFor(nodeRadius: number, pillWidth: number): number {
    return Math.max(2.6 * nodeRadius + NODE_PADDING, pillWidth + NODE_PADDING);
}

function ringCapacity(radius: number, spacing: number): number {
    return Math.max(1, Math.floor((2 * Math.PI * radius) / spacing));
}

type Band = "exile" | "manager" | "firefighter";

interface Geometry {
    size: number;
    center: number;
    exile: number;
    manager: number;
    outer: number;
    border: number;
}

const EXILE_RINGS: [number[], number[], number[]] = [
    [0.6],
    [0.34, 0.76],
    [0.22, 0.52, 0.8],
];
const MANAGER_RINGS: [number[], number[], number[]] = [
    [0.5],
    [0.3, 0.7],
    [0.2, 0.5, 0.8],
];

/** Sum of the two-sub-ring fractions per zone — how much usable arc a zone gets. */
const EXILE_ARC_FACTOR = EXILE_RINGS[1][0] + EXILE_RINGS[1][1];

// AIDEV-NOTE: Two-step: size each zone by the arc it actually needs, then scale the whole
// figure up so the outermost parts reach the edge of the canvas. Sizing alone leaves dead
// white space; scaling alone crushes the centre. Both together spend every available pixel
// on distance between parts, which is what keeps names and relation labels readable.
//
// Zones are NOT sized by part count alone — the exile disc and the manager annulus convert
// radius into usable arc at very different rates. A disc of radius R offers 2π·1.1R of
// sub-ring; an annulus from R to R+w offers 2π(2R + w). Ignoring that is what crushed
// eleven exiles into the middle while managers had room to spare.
function solveGeometry(
    size: number,
    counts: Record<Band, number>,
    nodeRadius: number,
    pillWidth: number,
    tuning: DpmpMapTuning,
): Geometry {
    const minGap = nodeRadius * 2 + NODE_PADDING;

    // The manager ring is the inscribed circle: as large as the square allows once an
    // avatar and its label clear the edge. Everything inside is sized against it.
    const manager =
        (size / 2 - nodeRadius - size * tuning.edgeMarginFraction) *
        tuning.managerRingOverflow;

    // AIDEV-NOTE: The exile disc takes what its own count needs to be readable and NOT ONE
    // PIXEL MORE — everything it gives back widens the manager annulus, which is the whole
    // point of pushing the manager ring out to the page edge.
    //
    // Sizing it relative to the managers (equalising arc per part) is wrong and degenerates:
    // as exile count approaches manager count the solution runs away and the disc swallows
    // the map. Exiles are sized against themselves only.
    //
    // Arc available on the two exile sub-rings is 2π·k·Re, and each exile wants its avatar
    // plus label plus a readability margin, so Re = Ne·spacing·comfort / (2π·k).
    // AIDEV-NOTE: `exileMaxShare` is a HARD CAP here, not just an accept/reject test in the
    // tier loop. It used to be only the latter, and when no avatar tier could satisfy it the
    // loop fell through to its pre-loop fallback: the SMALLEST avatar with the centre still
    // oversized. So a big exile count shrank every avatar on the map AND kept the bloated
    // disc — the exact opposite of the intent. Capping the disc makes the test satisfiable,
    // which lets the tier loop pick an avatar size on real fit.
    const maxExile = Math.min(manager - minGap, manager * tuning.exileMaxShare);
    const spacing = nodeSpacingFor(nodeRadius, pillWidth);
    // AIDEV-NOTE: Three floors, whichever is largest: a fraction of the canvas, a multiple
    // of one avatar (so a lone exile sits in a circle visibly bigger than itself rather
    // than wearing it as a collar), and enough circumference for the reserved "EXILES"
    // label arc to actually hold the word — parts are kept out of that window, so if the
    // window is too short for the text the label spills into occupied arc.
    const labelArcNeed = ringLabelArcLength() / RING_LABEL_MAX_ARC;
    const exileFloor = Math.max(
        size * tuning.minExileFraction,
        nodeRadius * tuning.minExileAvatarMultiple,
        tuning.reserveRingLabelArc ? labelArcNeed : 0,
    );
    const exile =
        counts.exile === 0
            ? Math.min(exileFloor, maxExile)
            : Math.max(
                  Math.min(exileFloor, maxExile),
                  Math.min(
                      (counts.exile * spacing * tuning.exileComfort) /
                          (2 * Math.PI * EXILE_ARC_FACTOR),
                      maxExile,
                  ),
              );

    return {
        size,
        center: size / 2,
        exile,
        manager,
        // AIDEV-NOTE: Firefighters and border clouds live OUTSIDE the inscribed circle,
        // in the four corners the square has left over. `outer` is the furthest a corner
        // node may sit along a diagonal; see cornerLimit().
        outer: tuning.cornerOverflowZones
            ? cornerLimit(size, nodeRadius, tuning.edgeMarginFraction)
            : size * tuning.borderFraction - nodeRadius - NODE_PADDING,
        border: size * tuning.borderFraction,
    };
}

interface BandSpec {
    r0: number;
    r1: number;
    /** Sub-ring positions (fractions of the band) for 1, 2 and 3 rings. */
    rings: [number[], number[], number[]];
    /** Start angle, staggered per band so adjacent bands don't line up radially. */
    start: number;
}

function bandSpecs(g: Geometry): Record<Band, BandSpec> {
    return {
        exile: {
            r0: 0,
            r1: g.exile,
            rings: EXILE_RINGS,
            start: -Math.PI / 2 + 0.15,
        },
        manager: {
            r0: g.exile,
            r1: g.manager,
            rings: MANAGER_RINGS,
            start: -Math.PI / 2 + 0.45,
        },
        // AIDEV-NOTE: Uncontained by design — nothing is stroked at `outer`, it only
        // bounds placement so firefighters stay clear of the border clouds.
        firefighter: {
            r0: g.manager,
            r1: g.outer,
            rings: MANAGER_RINGS,
            start: -Math.PI / 2 + 0.8,
        },
    };
}

const LINE_START: Record<"unknown-boundary" | "protector-boundary", number> = {
    "unknown-boundary": -Math.PI / 2 + 1.25,
    "protector-boundary": -Math.PI / 2 + 1.6,
};

function bandRadii(spec: BandSpec, ringCount: number): number[] {
    const width = spec.r1 - spec.r0;
    return spec.rings[ringCount - 1].map((f) => spec.r0 + f * width);
}

/** Below this, a band stays on a single ring — two rows of three looks like an accident. */
const MIN_COUNT_FOR_TWO_ROWS = 8;

/**
 * Sub-rings to actually USE. `ringsNeeded` answers the minimum that fits, which packs a
 * crowded band onto one circumference and leaves the rest of the annulus empty. When the
 * band is wide enough for two rows and there are enough parts to justify it, spread across
 * two — the parts end up markedly further apart both angularly and radially.
 */
function subRingsToUse(
    spec: BandSpec,
    count: number,
    nodeRadius: number,
    spacing: number,
): number {
    const needed = ringsNeeded(spec, count, spacing) || 3;
    if (needed > 1) return needed;
    const rowHeight = 2 * nodeRadius + NODE_PADDING;
    const fitsTwoRows = spec.r1 - spec.r0 >= 2 * rowHeight;
    return count >= MIN_COUNT_FOR_TWO_ROWS && fitsTwoRows ? 2 : 1;
}

/** How many sub-rings a band needs for `count` nodes at `nodeRadius`; 0 = doesn't fit in 3. */
function ringsNeeded(spec: BandSpec, count: number, spacing: number): number {
    for (let rings = 1; rings <= 3; rings += 1) {
        const capacity = bandRadii(spec, rings).reduce(
            (sum, r) => sum + ringCapacity(r, spacing),
            0,
        );
        if (count <= capacity) return rings;
    }
    return 0;
}

/** Largest node radius (≤ max) at which every band fits in at most three sub-rings. */
function chooseNodeRadius(
    specs: Record<Band, BandSpec>,
    bandCounts: Record<Band, number>,
    maxNodeRadius: number,
): number {
    const tiers = RADIUS_TIERS.filter((tier) => tier <= maxNodeRadius);
    if (tiers.length === 0) tiers.push(maxNodeRadius);
    for (const tier of tiers) {
        const fits = (Object.keys(bandCounts) as Band[]).every(
            (band) =>
                bandCounts[band] === 0 ||
                ringsNeeded(specs[band], bandCounts[band], tier) > 0,
        );
        if (fits) return tier;
    }
    return tiers[tiers.length - 1];
}

interface Placed {
    x: number;
    y: number;
}

function onRing(g: Geometry, radius: number, angle: number): Placed {
    return {
        x: g.center + radius * Math.cos(angle),
        y: g.center + radius * Math.sin(angle),
    };
}

function placeRing(
    g: Geometry,
    count: number,
    radius: number,
    start: number,
): Placed[] {
    const step = (2 * Math.PI) / count;
    return Array.from({ length: count }, (_, i) =>
        onRing(g, radius, start + i * step),
    );
}

/** Evenly spread `count` nodes across an arc, leaving both ends clear. */
function placeArc(
    g: Geometry,
    count: number,
    radius: number,
    start: number,
    sweep: number,
): Placed[] {
    if (count === 1) return [onRing(g, radius, start + sweep / 2)];
    const step = sweep / count;
    return Array.from({ length: count }, (_, i) =>
        onRing(g, radius, start + step * (i + 0.5)),
    );
}

// AIDEV-NOTE: Corner placement for the uncontained zones. Michelle draws firefighters
// loose outside the manager circle; on a square page the only room left there is the four
// corners, which is also space a concentric ring can never reach. Nodes are dealt round
// robin across the corners so they stay balanced, then fanned within each corner.
function placeCorners(
    g: Geometry,
    count: number,
    innerBound: number,
    nodeRadius: number,
): Placed[] {
    if (count === 0) return [];
    const step = nodeRadius * 2 + NODE_PADDING;
    const outerBound = Math.max(innerBound + step, g.outer);
    return Array.from({ length: count }, (_, i) => {
        const corner = CORNER_ANGLES[i % CORNER_ANGLES.length];
        const depth = Math.floor(i / CORNER_ANGLES.length);
        // Alternate sides of the diagonal so a second node in a corner does not stack
        // directly behind the first.
        const fan = depth === 0 ? 0 : (depth % 2 === 1 ? 1 : -1) * 0.22;
        const radius = Math.min(
            outerBound,
            innerBound + step * (0.6 + Math.floor(depth / 2)),
        );
        // AIDEV-NOTE: Nudge off the two pieces of chrome a corner can land on — the ring
        // labels ride the upper-left arc, and the "Self" caption runs along the bottom.
        // The arc reservation used by the bands does not cover corners, since corner
        // parts sit outside every ring.
        const placedAt = onRing(g, radius, corner + fan);
        const labelArc = ringLabelArcAt(radius);
        const nearLabelArc =
            Math.abs(
                Math.atan2(placedAt.y - g.center, placedAt.x - g.center) -
                    (RING_LABEL_ANGLE - 2 * Math.PI),
            ) < labelArc;
        const overCaption =
            placedAt.y > g.size - CAPTION_FONT_SIZE - CAPTION_GAP - 14;
        if (!nearLabelArc && !overCaption) return placedAt;
        const shifted = onRing(
            g,
            radius,
            corner + fan + (nearLabelArc ? labelArc : 0),
        );
        return shifted.y > g.size - CAPTION_FONT_SIZE - CAPTION_GAP - 14
            ? { x: shifted.x, y: g.size - CAPTION_FONT_SIZE - CAPTION_GAP - 14 }
            : shifted;
    });
}

/**
 * Place `count` nodes on a ring at angles that dodge what is already on the map and stay
 * inside the canvas.
 *
 * AIDEV-NOTE: Boundary parts (unclear classification) sit ON a ring line rather than in a
 * band, and they are placed AFTER the bands — but spreading them evenly from a fixed start
 * angle ignored the band parts already there, so they landed on top of them. The manager
 * ring also overflows the square, so parts of that circle are off-canvas entirely. Score
 * candidate angles by clearance from placed nodes, reject any that do not fit the page,
 * then take them greedily with a minimum separation. Deterministic: fixed sample count,
 * fixed scan order, no RNG.
 */
function placeRingAvoiding(
    g: Geometry,
    count: number,
    radius: number,
    start: number,
    occupied: Array<Placed & { halfWidth: number }>,
    clearance: number,
    halfWidth: number,
    rowHeight: number,
): Placed[] {
    const SAMPLES = 240;
    const scored: { angle: number; place: Placed; clear: number }[] = [];
    for (let i = 0; i < SAMPLES; i += 1) {
        const angle = start + (i * 2 * Math.PI) / SAMPLES;
        const place = onRing(g, radius, angle);
        if (
            place.x < clearance ||
            place.x > g.size - clearance ||
            place.y < clearance ||
            place.y > g.size - clearance
        ) {
            continue;
        }
        // AIDEV-NOTE: Chrome is not a node, so node-clearance scoring cannot see it. The
        // ring labels ride the upper-left arc and the "Self" caption runs along the
        // bottom; a candidate landing on either is rejected outright rather than merely
        // scored down, because there is no amount of clearance that makes it acceptable.
        if (place.y > g.size - CAPTION_FONT_SIZE - CAPTION_GAP - 14) continue;
        const angleFromCentre = Math.atan2(
            place.y - g.center,
            place.x - g.center,
        );
        const labelDelta = Math.abs(
            ((angleFromCentre - RING_LABEL_ANGLE + Math.PI * 3) %
                (2 * Math.PI)) -
                Math.PI,
        );
        // Boundary parts sit ON a labelled ring line, so they always dodge the label —
        // but only by as much arc as the word actually needs at this radius.
        if (labelDelta < ringLabelArcAt(radius) / 2) continue;
        // AIDEV-NOTE: Clearance is measured against PILL extents, not node centres. Pills
        // are far wider than they are tall, so centre distance rates a spot as clear when
        // the two names would still overlap horizontally. Normalising each axis by the
        // extent that matters makes 1.0 mean "just touching".
        let clear = Infinity;
        for (const other of occupied) {
            const dx =
                Math.abs(other.x - place.x) / (other.halfWidth + halfWidth);
            const dy = Math.abs(other.y - place.y) / rowHeight;
            const d = hypot(dx, dy);
            if (d < clear) clear = d;
        }
        scored.push({ angle, place, clear });
    }
    // Nothing on this ring fits the page — fall back so a part is never dropped.
    if (scored.length === 0) return placeRing(g, count, radius, start);

    scored.sort((a, b) => b.clear - a.clear || a.angle - b.angle);
    const minSeparation = (2 * Math.PI) / Math.max(count, 1) / 1.6;
    const chosen: { angle: number; place: Placed }[] = [];
    for (const candidate of scored) {
        if (chosen.length >= count) break;
        const tooClose = chosen.some((c) => {
            const delta = Math.abs(c.angle - candidate.angle) % (2 * Math.PI);
            return Math.min(delta, 2 * Math.PI - delta) < minSeparation;
        });
        if (!tooClose) chosen.push(candidate);
    }
    // Relax the separation rule rather than drop a part.
    for (const candidate of scored) {
        if (chosen.length >= count) break;
        if (!chosen.some((c) => c.angle === candidate.angle)) {
            chosen.push(candidate);
        }
    }
    return chosen.slice(0, count).map((c) => c.place);
}

function placeBand(
    g: Geometry,
    count: number,
    spec: BandSpec,
    nodeRadius: number,
    spacing: number,
    reserveLabelArc = false,
): Placed[] {
    if (count === 0) return [];
    // AIDEV-NOTE: A lone exile sits at the exact centre; other bands never get r = 0.
    if (count === 1 && spec.r0 === 0) return [{ x: g.center, y: g.center }];

    const rings = subRingsToUse(spec, count, nodeRadius, spacing);
    const radii = bandRadii(spec, rings);
    // The ring label rides the band's OUTER boundary; only a sub-ring level with it dodges.
    const dodges = (radius: number) =>
        reserveLabelArc && underRingLabel(radius, spec.r1, nodeRadius);
    const arcPlace = (n: number, radius: number) => {
        const arc = ringLabelArcAt(radius);
        return placeArc(
            g,
            n,
            radius,
            RING_LABEL_ANGLE + arc / 2,
            2 * Math.PI - arc,
        );
    };
    if (rings === 1) {
        return dodges(radii[0])
            ? arcPlace(count, radii[0])
            : placeRing(g, count, radii[0], spec.start);
    }

    // Split proportionally to circumference — the outer row is longer, so it carries more
    // parts — then interleave: each row is offset by HALF the step of the row outside it,
    // so an inner part sits in the gap between two outer parts rather than directly
    // beneath one. Offsetting by a row's own step (the previous behaviour) does not
    // interleave when the two rows hold different counts, which is the normal case.
    const total = radii.reduce((s, r) => s + r, 0);
    const counts = radii.map((r) => Math.round((count * r) / total));
    let drift = count - counts.reduce((s, c) => s + c, 0);
    for (let i = counts.length - 1; drift !== 0 && i >= 0; i -= 1) {
        counts[i] += Math.sign(drift);
        drift -= Math.sign(drift);
    }
    const placed: Placed[] = [];
    radii.forEach((r, i) => {
        if (counts[i] <= 0) return;
        const outerCount = i + 1 < counts.length ? counts[i + 1] : 0;
        const stagger = outerCount > 0 ? Math.PI / outerCount : 0;
        if (dodges(r)) {
            placed.push(...arcPlace(counts[i], r));
            return;
        }
        placed.push(...placeRing(g, counts[i], r, spec.start + stagger));
    });
    return placed;
}

// AIDEV-NOTE: sqrt, not Math.hypot — hypot is not bit-identical across JS engines, and
// the last float digit differing between the server and the browser breaks hydration.
function hypot(dx: number, dy: number): number {
    return Math.sqrt(dx * dx + dy * dy);
}

function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

// AIDEV-NOTE: Code-point comparison, not localeCompare — the server and the browser can run
// under different locales, and a different node order would break hydration.
function compareStrings(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
}

function byName(a: DpmpMapNodeInput, b: DpmpMapNodeInput): number {
    return compareStrings(a.name, b.name) || compareStrings(a.id, b.id);
}

export function estimateTextWidth(text: string, fontSize: number): number {
    return text.length * fontSize * 0.56 + 6;
}

function truncate(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * How a relationship should read on a drawn line: the word, and whether it points.
 *
 * AIDEV-NOTE: Michelle Glass's rule (2026-09-09) — a PROTECTION relationship needs an
 * arrow, because "the protector knows the exile, but the exile doesn't yet know the
 * protector", and she has fielded that question in her workshops since the beginning.
 * Alliances and polarizations are mutual, so the word alone carries them and an arrow
 * would imply a direction that isn't there. Types come from part_relationship.type;
 * unknown/legacy values fall back to the raw type, undirected, rather than vanishing.
 */
export function describeRelationship(type: string): {
    label: string;
    directed: boolean;
} {
    switch (type) {
        case "protects":
            return { label: "protects", directed: true };
        case "controls-access-to":
            return { label: "controls access to", directed: true };
        case "supports":
            return { label: "supports", directed: true };
        case "supported-by":
            return { label: "supported by", directed: true };
        case "polarized-with":
            return { label: "polarized with", directed: false };
        case "allied-with":
            return { label: "allied with", directed: false };
        default:
            // Legacy free-text values ("Protecting", "Conflictual", …) still exist in
            // production rows; show them as written rather than dropping the line's meaning.
            return {
                label: type.replace(/-/g, " ").toLowerCase(),
                directed: false,
            };
    }
}

function spokeMark(type: string): DpmpMapSpoke["mark"] {
    if (type === "polarized-with") return "P";
    if (type === "allied-with") return "A";
    return "";
}

/** Spread sorted angles to a minimum separation; falls back to even spacing when they'd wrap. */
function spreadAngles(sorted: number[]): number[] {
    const n = sorted.length;
    if (n <= 1) return sorted;
    const minSep = Math.min(SPOKE_MIN_SEPARATION, (2 * Math.PI) / n);
    const out = [...sorted];
    for (let i = 1; i < n; i += 1) {
        if (out[i] - out[i - 1] < minSep) out[i] = out[i - 1] + minSep;
    }
    if (out[n - 1] - out[0] > 2 * Math.PI - minSep) {
        const step = (2 * Math.PI) / n;
        return out.map((_, i) => out[0] + i * step);
    }
    return out;
}

interface Constraint {
    kind: "band" | "line" | "border" | "fixed";
    rMin: number;
    rMax: number;
}

// AIDEV-NOTE: Collision relaxation — push overlapping nodes apart, then pull each one back
// into its zone: band nodes may drift anywhere inside their band, line nodes stay on their
// exact ring line, border nodes stay on the border radius, a lone centred exile stays put.
// Fixed iteration count and pair order keep it deterministic. This is what makes a dense map
// look "organically" spaced rather than piled on a few concentric circles.
interface PillHalfExtent {
    /** Half the pill's width — the real horizontal footprint of a named part. */
    hw: number;
    /** Half the pill's height. */
    hh: number;
}

function relax(
    g: Geometry,
    nodes: DpmpMapNode[],
    constraints: Constraint[],
    pills?: PillHalfExtent[],
): void {
    const n = nodes.length;
    for (let iter = 0; iter < RELAX_ITERATIONS; iter += 1) {
        const strength = 0.5;
        for (let i = 0; i < n; i += 1) {
            for (let j = i + 1; j < n; j += 1) {
                const a = nodes[i];
                const b = nodes[j];
                const minDist = a.radius + b.radius + NODE_PADDING;
                let dx = b.x - a.x;
                let dy = b.y - a.y;
                let dist = hypot(dx, dy);
                if (dist === 0) {
                    // Coincident: separate along a deterministic direction.
                    const angle = ((i * 7 + j * 13) % 360) * (Math.PI / 180);
                    dx = Math.cos(angle);
                    dy = Math.sin(angle);
                    dist = 1;
                }
                if (dist < minDist) {
                    const push = ((minDist - dist) / dist) * strength;
                    const mx = dx * push * 0.5;
                    const my = dy * push * 0.5;
                    if (constraints[i].kind !== "fixed") {
                        a.x -= mx;
                        a.y -= my;
                    }
                    if (constraints[j].kind !== "fixed") {
                        b.x += mx;
                        b.y += my;
                    }
                }

                // AIDEV-NOTE: The circular push above separates AVATARS. What a reader
                // sees collide is the NAME PILL, which is roughly twice as wide as the
                // avatar it hangs off — so two parts cleanly separated by radius+padding
                // still had their names printed over each other. This is the pass that
                // was missing, and no amount of preset tuning substituted for it.
                // Pills are wide and short, so pushing along the axis of least
                // penetration moves ring neighbours sideways, which is where the room is.
                if (!pills) continue;
                const pa = pills[i];
                const pb = pills[j];
                const pdx = b.x - a.x;
                const pdy = b.y + b.radius - (a.y + a.radius);
                const ox = pa.hw + pb.hw - Math.abs(pdx);
                const oy = pa.hh + pb.hh - Math.abs(pdy);
                if (ox <= 0 || oy <= 0) continue;
                const aFixed = constraints[i].kind === "fixed";
                const bFixed = constraints[j].kind === "fixed";
                if (aFixed && bFixed) continue;
                const share = aFixed || bFixed ? 1 : 0.5;
                if (ox < oy) {
                    const shift = (pdx >= 0 ? ox : -ox) * strength * share;
                    if (!aFixed) a.x -= shift;
                    if (!bFixed) b.x += shift;
                } else {
                    const shift = (pdy >= 0 ? oy : -oy) * strength * share;
                    if (!aFixed) a.y -= shift;
                    if (!bFixed) b.y += shift;
                }
            }
        }
        for (let i = 0; i < n; i += 1) {
            const c = constraints[i];
            if (c.kind === "fixed") continue;
            const node = nodes[i];
            const dx = node.x - g.center;
            const dy = node.y - g.center;
            const r = hypot(dx, dy) || 1;
            let target = r;
            if (c.kind === "line" || c.kind === "border") target = c.rMin;
            else target = Math.min(c.rMax, Math.max(c.rMin, r));
            if (target !== r) {
                node.x = g.center + (dx / r) * target;
                node.y = g.center + (dy / r) * target;
            }
            // AIDEV-NOTE: The manager ring overflows the square on purpose, so a node on
            // its outer sub-ring can land off-page near a cardinal direction. Clamp into
            // the canvas — the ring clips, the parts never do. Horizontally the NAME PILL
            // is what has to fit, not the avatar: it is wider, so clamping to the avatar
            // alone still let names run off the page.
            const halfPill = pills?.[i].hw ?? node.radius;
            node.x = Math.min(
                Math.max(node.x, halfPill + 2),
                g.size - halfPill - 2,
            );
            const edgeY = node.radius + 2;
            node.y = Math.min(Math.max(node.y, edgeY), g.size - edgeY);
        }
    }
}

interface Box {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
}

function overlapArea(a: Box, b: Box): number {
    const w = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
    const h = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
    return w > 0 && h > 0 ? w * h : 0;
}

/** Wrap a part name into at most two lines; leftovers ellipsise the second. */
// AIDEV-NOTE: Tolerates null/undefined — a nameless part reaching layout
// during SSR once took down the whole session page.
export function wrapPartName(
    name: string | null | undefined,
    maxChars: number,
): string[] {
    const safeName = name ?? "";
    const words = safeName.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [safeName];
    const lines: string[] = [];
    let current = "";
    for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (!current || candidate.length <= maxChars) {
            current = candidate;
            continue;
        }
        lines.push(current);
        current = word;
        if (lines.length === 2) break;
    }
    if (lines.length < 2 && current) lines.push(current);
    const placed = lines.join(" ").length;
    if (placed < safeName.trim().length) {
        const last = lines.length - 1;
        lines[last] = truncate(`${lines[last]}\u2026`, maxChars);
    }
    return lines.slice(0, 2);
}

/** Geometry of a name pill, in the node's own frame. Mirrors NamePill in the renderer. */
export function pillBox(
    node: Pick<DpmpMapNode, "x" | "y" | "radius" | "name">,
    nameFontSize: number,
    nameMaxChars: number,
): Box {
    const lines = wrapPartName(node.name, nameMaxChars);
    const lineHeight = nameFontSize + 1.5;
    const height = lines.length * lineHeight + 6;
    const width =
        Math.max(
            ...lines.map((line) => estimateTextWidth(line, nameFontSize)),
        ) + 12;
    const top = node.y + node.radius - height / 2;
    return {
        x0: node.x - width / 2,
        y0: top,
        x1: node.x + width / 2,
        y1: top + height,
    };
}

// AIDEV-NOTE: Names are pills pinned to their avatar's bottom edge, so there is nothing to
// place — but how many of them OVERLAP still drives whether the avatar tier steps down.
// Counting pill-on-pill overlap keeps that retry honest; it used to measure a radial label
// layout that no longer exists.
function countPillCollisions(
    nodes: DpmpMapNode[],
    nameFontSize: number,
    nameMaxChars: number,
): number {
    const boxes = nodes.map((node) =>
        pillBox(node, nameFontSize, nameMaxChars),
    );
    const collided = new Set<number>();
    for (let i = 0; i < boxes.length; i += 1) {
        for (let j = i + 1; j < boxes.length; j += 1) {
            if (overlapArea(boxes[i], boxes[j]) > 4) {
                collided.add(i);
                collided.add(j);
            }
        }
    }
    return collided.size;
}

export function computeDpmpMapLayout(
    nodesInput: DpmpMapNodeInput[],
    edgesInput: DpmpMapEdgeInput[],
    options: DpmpMapLayoutOptions = {},
): DpmpMapLayout {
    const size = options.size ?? DPMP_MAP_DEFAULT_SIZE;
    const tuning = DPMP_MAP_TUNING[options.context ?? "system"];
    // AIDEV-NOTE: The cap keys off parts per unit of CANVAS, not raw count — the same 55
    // parts fit 16-character names with zero collisions full-screen and cannot hold 10 on
    // the card. Counting parts alone made the card legible and the full-screen view
    // needlessly stingy. Normalised to the default 1000px canvas so the preset numbers
    // still read as "parts on a normal-sized map".
    const densityCount =
        nodesInput.length * (DPMP_MAP_DEFAULT_SIZE / Math.max(size, 1)) ** 2;
    const densityNameCap =
        tuning.nameCharSteps.find((step) => densityCount <= step.upTo)?.chars ??
        8;
    const nameCharCap = Math.min(
        densityNameCap,
        options.nameMaxChars ?? Number.POSITIVE_INFINITY,
    );

    const grouped: Record<DpmpMapZone, DpmpMapNodeInput[]> = {
        exile: [],
        manager: [],
        firefighter: [],
        "protector-boundary": [],
        "unknown-boundary": [],
        guide: [],
        "unattached-burden": [],
    };
    for (const node of nodesInput) {
        grouped[resolveDpmpMapZone(node.type)].push(node);
    }
    for (const zone of Object.keys(grouped) as DpmpMapZone[]) {
        grouped[zone].sort(byName);
    }

    const bandCounts: Record<Band, number> = {
        exile: grouped.exile.length,
        manager: grouped.manager.length,
        firefighter: grouped.firefighter.length,
    };

    // AIDEV-NOTE: Radius and geometry are mutually dependent — ring radii are solved from
    // the population at a given avatar size, and whether that size fits depends on the
    // rings it produced. Resolve by trying avatar sizes largest-first and keeping the
    // first whose solved geometry actually holds every band; the smallest tier is the
    // floor so a cramped canvas still draws visible avatars rather than dots.
    const tiers = RADIUS_TIERS.filter(
        (tier) => tier <= (options.maxNodeRadius ?? RADIUS_TIERS[0]),
    );
    if (tiers.length === 0)
        tiers.push(options.maxNodeRadius ?? RADIUS_TIERS[0]);

    // Pill width at a given avatar size — the real spacing driver.
    const pillWidthFor = (tier: number) =>
        estimateTextWidth(
            "n".repeat(Math.min(nameMaxCharsFor(tier), nameCharCap)),
            nameFontSizeFor(tier),
        ) + 12;

    let nodeRadius = tiers[tiers.length - 1];
    let g = solveGeometry(
        size,
        bandCounts,
        nodeRadius,
        pillWidthFor(nodeRadius),
        tuning,
    );
    for (const tier of tiers) {
        const tierSpacing = nodeSpacingFor(tier, pillWidthFor(tier));
        const candidate = solveGeometry(
            size,
            bandCounts,
            tier,
            pillWidthFor(tier),
            tuning,
        );
        const candidateSpecs = bandSpecs(candidate);
        const bandsFit = (Object.keys(bandCounts) as Band[]).every(
            (band) =>
                bandCounts[band] === 0 ||
                ringsNeeded(
                    candidateSpecs[band],
                    bandCounts[band],
                    tierSpacing,
                ) > 0,
        );
        // AIDEV-NOTE: A tier that only "fits" by letting the exile disc crowd out the
        // manager annulus is not a fit. Rejecting it here is what makes a big exile count
        // shrink the avatars rather than inflate the centre.
        const centreIsRestrained =
            candidate.exile <= candidate.manager * tuning.exileMaxShare;
        const fits = bandsFit && centreIsRestrained;
        if (fits) {
            nodeRadius = tier;
            g = candidate;
            break;
        }
    }
    const specs = bandSpecs(g);
    const spacingForLayout = nodeSpacingFor(
        nodeRadius,
        pillWidthFor(nodeRadius),
    );
    const nameFontSize = nameFontSizeFor(nodeRadius);
    const nameMaxChars = Math.min(nameMaxCharsFor(nodeRadius), nameCharCap);

    const nodes: DpmpMapNode[] = [];
    const constraints: Constraint[] = [];
    const push = (
        input: DpmpMapNodeInput,
        zone: DpmpMapZone,
        placed: Placed,
        options: {
            radius: number;
            onLine: boolean;
            cloud: boolean;
            dashed: boolean;
            constraint: Constraint;
        },
    ) => {
        nodes.push({
            id: input.id,
            name: input.name,
            imageUrl: input.imageUrl ?? null,
            zone,
            x: placed.x,
            y: placed.y,
            radius: options.radius,
            onLine: options.onLine,
            cloud: options.cloud,
            dashed: options.dashed,
            spokes: [],
        });
        constraints.push(options.constraint);
    };

    for (const zone of ["exile", "manager"] as const) {
        const items = grouped[zone];
        const spec = specs[zone];
        const placed = placeBand(
            g,
            items.length,
            spec,
            nodeRadius,
            spacingForLayout,
            // Every ring label rides at the same 12% offset, so both drawn rings need the
            // arc kept clear — not just the exile one.
            zone === "exile" || zone === "manager",
        );
        items.forEach((item, i) =>
            push(item, zone, placed[i], {
                radius: nodeRadius,
                onLine: false,
                cloud: false,
                dashed: false,
                constraint:
                    items.length === 1 && spec.r0 === 0
                        ? { kind: "fixed", rMin: 0, rMax: 0 }
                        : {
                              kind: "band",
                              rMin:
                                  spec.r0 === 0 ? 0 : spec.r0 + nodeRadius + 6,
                              rMax: spec.r1 - nodeRadius - 6,
                          },
            }),
        );
    }

    // AIDEV-NOTE: Firefighters are not a band — they sit in the four corners outside the
    // inscribed manager ring, which lets that ring grow to the page edge instead of
    // reserving a concentric lane that a handful of parts would leave mostly empty.
    {
        const items = grouped.firefighter;
        const inner = g.manager + nodeRadius + NODE_PADDING;
        // AIDEV-NOTE: On the whole-system map the corners cannot absorb a real firefighter
        // population, so they keep a concentric lane between the manager ring and the
        // border instead. Corners are a scoped-map luxury.
        const placed = tuning.cornerOverflowZones
            ? placeCorners(g, items.length, inner, nodeRadius)
            : placeRing(
                  g,
                  items.length,
                  (inner + g.outer) / 2,
                  -Math.PI / 2 + 0.8,
              );
        items.forEach((item, i) =>
            push(item, "firefighter", placed[i], {
                radius: nodeRadius,
                onLine: false,
                cloud: false,
                dashed: false,
                constraint: { kind: "band", rMin: inner, rMax: g.outer },
            }),
        );
    }

    for (const zone of ["unknown-boundary", "protector-boundary"] as const) {
        const items = grouped[zone];
        if (items.length === 0) continue;
        const radius = zone === "unknown-boundary" ? g.exile : g.manager;
        // Dodge the band parts already placed, and stay on the page.
        const placed = placeRingAvoiding(
            g,
            items.length,
            radius,
            LINE_START[zone],
            nodes.map((node) => {
                const box = pillBox(node, nameFontSize, nameMaxChars);
                return {
                    x: node.x,
                    y: node.y,
                    halfWidth: (box.x1 - box.x0) / 2,
                };
            }),
            nodeRadius * 0.85 + NODE_PADDING,
            estimateTextWidth("nnnnnnnnnnnn", nameFontSize) / 2 + 6,
            nodeRadius * 2 + NODE_PADDING,
        );
        items.forEach((item, i) =>
            push(item, zone, placed[i], {
                radius: nodeRadius * 0.85,
                onLine: true,
                cloud: false,
                dashed: true,
                constraint: { kind: "line", rMin: radius, rMax: radius },
            }),
        );
    }

    // AIDEV-NOTE: Border placement is shared so guides and unattached burdens never
    // overlap; guides come first, starting at the top (Michelle draws the guide up there).
    const border = [
        ...grouped.guide.map((item) => ({ item, zone: "guide" as const })),
        ...grouped["unattached-burden"].map((item) => ({
            item,
            zone: "unattached-burden" as const,
        })),
    ];
    if (border.length > 0) {
        // AIDEV-NOTE: Also cornered, and further out than the firefighters — Michelle
        // draws guides and unattached burdens nearest the paper's edge. They start one
        // corner round further so a lone guide and a lone firefighter do not stack.
        const cloudRadius = nodeRadius * 0.9;
        const inner = g.manager + cloudRadius + NODE_PADDING;
        const placed = tuning.cornerOverflowZones
            ? placeCorners(g, border.length + 1, inner, cloudRadius).slice(1)
            : // AIDEV-NOTE: Just outside the manager ring, NOT at g.border. On the
              // whole-system map the manager ring already runs near the page edge and
              // the pills are wide, so a lane at g.border gets clamped back inside the
              // ring it is supposed to sit beyond — the clouds ended up among the
              // managers. Hugging the ring keeps the ordering true at any density.
              placeRing(g, border.length, inner, -Math.PI / 2);
        border.forEach(({ item, zone }, i) =>
            push(item, zone, placed[i], {
                radius: cloudRadius,
                onLine: false,
                cloud: true,
                dashed: zone === "unattached-burden",
                constraint: {
                    kind: "band",
                    rMin: inner,
                    rMax: cornerLimit(
                        g.size,
                        cloudRadius,
                        tuning.edgeMarginFraction,
                    ),
                },
            }),
        );
    }

    // Each node's pill half-extent — the real footprint the relaxation has to keep apart,
    // and what has to fit inside the page horizontally.
    const pills: PillHalfExtent[] = nodes.map((node) => {
        const box = pillBox(node, nameFontSize, nameMaxChars);
        return { hw: (box.x1 - box.x0) / 2, hh: (box.y1 - box.y0) / 2 };
    });
    relax(g, nodes, constraints, pills);

    // AIDEV-NOTE: The caption guard during placement tests the node CENTRE, but the pill
    // hangs below the avatar — so a node whose centre cleared the line still dropped its
    // name onto "Self — around all parts". Clamp on the pill's real bottom edge, after
    // relaxation, and only for nodes whose pill actually reaches the caption's own box.
    const caption = captionBox(g.size);
    for (const node of nodes) {
        const box = pillBox(node, nameFontSize, nameMaxChars);
        if (box.x0 > caption.x1) continue;
        const overshoot = box.y1 - caption.y0;
        if (overshoot > 0) node.y -= overshoot;
    }

    // Nothing may hang off the bottom of the page, caption or not.
    for (const node of nodes) {
        const box = pillBox(node, nameFontSize, nameMaxChars);
        const overshoot = box.y1 - (g.size - 2);
        if (overshoot > 0) node.y -= overshoot;
    }

    // Emit rounded coordinates so server and client render identical attribute strings.
    for (const node of nodes) {
        node.x = round2(node.x);
        node.y = round2(node.y);
    }
    const labelCollisions = countPillCollisions(
        nodes,
        nameFontSize,
        nameMaxChars,
    );

    const byId = new Map(nodes.map((node) => [node.id, node] as const));
    const pending = new Map<string, DpmpMapSpoke[]>();
    const spokeFontSize = spokeFontSizeFor(nodeRadius);
    const addSpoke = (
        from: DpmpMapNode,
        to: DpmpMapNode,
        type: string,
        fromSource: boolean,
    ) => {
        const mark = spokeMark(type);
        const name = truncate(to.name, spokeLabelMaxCharsFor(nodeRadius));
        const label = mark ? `${name} (${mark})` : name;
        // AIDEV-NOTE: The spoke must be at least as long as its label plus a margin — the
        // renderer backs the label with a white rect the length of the spoke.
        const length = Math.min(
            SPOKE_MAX_LENGTH,
            Math.max(
                SPOKE_MIN_LENGTH,
                estimateTextWidth(label, spokeFontSize) + 8,
            ),
        );
        const list = pending.get(from.id) ?? [];
        list.push({
            targetId: to.id,
            label,
            mark,
            type,
            fromSource,
            angle: Math.atan2(to.y - from.y, to.x - from.x),
            length,
        });
        pending.set(from.id, list);
    };

    for (const edge of edgesInput) {
        const source = byId.get(edge.sourcePartId);
        const target = byId.get(edge.targetPartId);
        if (!source || !target || source === target) continue;
        addSpoke(source, target, edge.type, true);
        addSpoke(target, source, edge.type, false);
    }

    for (const node of nodes) {
        const spokes = pending.get(node.id);
        if (!spokes) continue;
        spokes.sort(
            (a, b) => a.angle - b.angle || compareStrings(a.label, b.label),
        );
        const angles = spreadAngles(spokes.map((spoke) => spoke.angle));
        node.spokes = spokes.map((spoke, i) => ({
            ...spoke,
            angle: angles[i],
        }));
    }

    // AIDEV-NOTE: When more than a quarter of the names still collide, smaller avatars buy
    // more room than any placement trick can — step the tier down and lay out again.
    const smallerTier = RADIUS_TIERS.find((tier) => tier < nodeRadius);
    if (labelCollisions > nodes.length * 0.25 && smallerTier !== undefined) {
        return computeDpmpMapLayout(nodesInput, edgesInput, {
            ...options,
            maxNodeRadius: smallerTier,
        });
    }

    const zoneCounts = { ...EMPTY_COUNTS };
    for (const node of nodes) zoneCounts[node.zone] += 1;

    return {
        size: g.size,
        center: g.center,
        rings: {
            exile: g.exile,
            manager: g.manager,
            outer: g.outer,
            border: g.border,
        },
        nodeRadius,
        spokeFontSize,
        nameFontSize,
        nameMaxChars,
        nodes,
        zoneCounts,
        labelCollisions,
    };
}
